/**
 * NotificationBell — header affordance that surfaces the user's notification
 * feed (spec §12).
 *
 * Behaviour:
 *   - Bell icon with an unread-count badge when count > 0
 *   - Click → opens a right-side drawer with the most recent 50 events
 *   - Each row: title, body, type-coloured dot, relative timestamp,
 *     "unread" indicator dot until clicked or marked-all-read
 *   - Click a row → mark that event read AND navigate to its deep link
 *     (entity drilldown / project page / no-op if no refs)
 *   - "Mark all read" button at the top wipes the unread count to 0
 *   - Polling happens at the query layer (useNotificationFeed) — every 30s
 *     while the tab is visible, paused on hidden
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  Inbox,
  PackageX,
  RefreshCcw,
  TrendingUp,
  Unlock,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationsRead,
  useNotificationFeed,
  type NotificationEvent,
  type NotificationType,
} from '@/lib/notification-queries';
import { notificationLinkPath } from '@/lib/notification-link';
import { formatDate } from '@procurement/shared';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const feed = useNotificationFeed({ limit: 50 });
  const unreadCount = feed.data?.unreadCount ?? 0;
  const markRead = useMarkNotificationsRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  const handleRowClick = (n: NotificationEvent): void => {
    // 1. Mark this single event read (server is idempotent — re-clicks
    //    don't hurt).
    if (n.readAt === null) {
      markRead.mutate({ notificationEventIds: [n.id] });
    }
    // 2. Navigate if a deep link exists.
    const path = notificationLinkPath(n);
    if (path) {
      setOpen(false);
      void navigate({ to: path });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'
        }
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-primary hover:bg-subtle"
      >
        {/* Patch P21 — bell uses text-primary (was text-secondary). The
         * old colour was technically readable but sat right at the WCAG
         * floor on the topbar background; users reported the icon as
         * "not visible". Stroke also bumped from 1.5 → 1.75. */}
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-danger-fg px-1 text-[10px] font-semibold leading-[18px] text-white"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        description={
          unreadCount > 0
            ? `${unreadCount} unread`
            : feed.isLoading
              ? 'Loading…'
              : 'All caught up'
        }
        footer={
          <Button
            variant="tertiary"
            size="compact"
            leftIcon={<CheckCheck className="h-4 w-4" />}
            onClick={() => markAllRead.mutate()}
            disabled={unreadCount === 0 || markAllRead.isPending}
            loading={markAllRead.isPending}
          >
            Mark all read
          </Button>
        }
      >
        {feed.isLoading ? (
          <div className="flex items-center justify-center py-10 text-tertiary">
            <Spinner className="h-4 w-4" />
          </div>
        ) : feed.error ? (
          <div className="py-6 text-sm text-danger-fg">
            Couldn't load notifications. We'll try again on the next poll.
          </div>
        ) : feed.data?.items.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-6 w-6" strokeWidth={1.5} />}
            title="No notifications yet"
            body="You'll see updates here as projects move through the pipeline."
          />
        ) : (
          <ul className="-mx-2 divide-y divide-border-default">
            {feed.data?.items.map((n) => (
              <NotificationRow
                key={n.id}
                event={n}
                onClick={() => handleRowClick(n)}
              />
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function NotificationRow({
  event,
  onClick,
}: {
  event: NotificationEvent;
  onClick: () => void;
}) {
  const unread = event.readAt === null;
  const Icon = iconForType(event.notificationType);
  const tone = toneForType(event.notificationType);

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-3 px-2 py-3 text-left transition-colors hover:bg-subtle',
          unread && 'bg-surface',
        )}
      >
        <span
          className={cn(
            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
            tone === 'danger' && 'bg-danger-subtle text-danger-fg',
            tone === 'warning' && 'bg-warning-subtle text-warning-fg',
            tone === 'success' && 'bg-success-subtle text-success-fg',
            tone === 'info' && 'bg-subtle text-secondary',
          )}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('truncate text-sm', unread ? 'font-semibold text-primary' : 'text-secondary')}>
              {event.title}
            </span>
            {unread ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-accent"
                aria-label="Unread"
              />
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-secondary">{event.body}</p>
          <p className="mt-1 text-[10px] uppercase tracking-wide text-secondary">
            {formatRelative(event.createdAt)}
          </p>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Type → icon / tone
// ---------------------------------------------------------------------------

function iconForType(t: NotificationType): typeof Bell {
  switch (t) {
    case 'BATCH_CANCELLED':
      return PackageX;
    case 'BATCH_REPLACED':
      return RefreshCcw;
    case 'INSPECTION_NON_ACCEPTED':
    case 'PARTS_SUBMISSION_REJECTED':
      return XCircle;
    case 'INVOICE_UNLOCKED':
      return Unlock;
    case 'ITEM_OVERDUE':
    case 'WARRANTY_END_APPROACHING':
      return AlertTriangle;
    case 'ENTITY_ENTERED_STAGE':
      return TrendingUp;
    case 'PARTS_SUBMISSION_PENDING':
      return Inbox;
    default:
      return Bell;
  }
}

type Tone = 'danger' | 'warning' | 'success' | 'info';

function toneForType(t: NotificationType): Tone {
  switch (t) {
    case 'BATCH_CANCELLED':
    case 'INSPECTION_NON_ACCEPTED':
    case 'PARTS_SUBMISSION_REJECTED':
      return 'danger';
    case 'ITEM_OVERDUE':
    case 'WARRANTY_END_APPROACHING':
      return 'warning';
    case 'INVOICE_UNLOCKED':
      return 'success';
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// Time formatting — relative, no external lib
// ---------------------------------------------------------------------------

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(1, Math.round((now - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  // Older than a month — show the actual date
  return formatDate(iso);
}
